/**
 * useUiMotion — single page-level hook that wires up the five opt-in
 * motion utilities defined in apps/web/src/styles/globals.css:
 *
 *   1. .reveal / .reveal-fold   — IntersectionObserver toggles
 *                                 .is-visible on first scroll into
 *                                 viewport. One-shot per element.
 *
 *   2. .has-light                — pointer position is recorded as
 *                                 --lx / --ly CSS vars (percentages)
 *                                 and .is-lit is toggled on
 *                                 mouseenter / mouseleave so the
 *                                 ::after radial-gradient lamp tracks
 *                                 the cursor.
 *
 *   3. [data-tilt]               — element rotates a few degrees
 *                                 toward the cursor while hovered
 *                                 (perspective transform). Resets
 *                                 on mouseleave. Cheap CSS-only —
 *                                 no rAF loop.
 *
 *   4. .has-cursor-gutter        — vertical pine bar in the element's
 *                                 LEFT MARGIN tracks the cursor's Y
 *                                 position. JS sets --gy (px) +
 *                                 toggles .gutter-active on
 *                                 mousemove / mouseleave. Use on
 *                                 long meeting lists so the eye has
 *                                 an anchor when scanning.
 *
 *   5. [data-parallax]           — pointer position is recorded as
 *                                 --mx / --my CSS vars on the
 *                                 element so a paired ::after
 *                                 radial-gradient (e.g. .hero-glow)
 *                                 tracks the cursor. Snaps back to
 *                                 50% on mouseleave so the rest
 *                                 state is centered. Used on the
 *                                 dashboard hero so the sage glow
 *                                 follows the pointer.
 *
 * Why a single hook instead of three? They all share the same
 * lifecycle (mount-on-page-render, scope to whatever's currently in
 * the DOM, prefers-reduced-motion guard) and observing the document
 * once is cheaper than three separate listeners. Call this from the
 * top of any page component that uses the corresponding utilities;
 * components themselves don't need to know about it.
 *
 * The hook is safe to call when none of the elements exist — the
 * listeners are no-ops in that case.
 */
import { useEffect } from 'react';

export function useUiMotion(): void {
  useEffect(() => {
    // Honor reduced-motion: skip the JS entirely. The CSS already
    // collapses the .reveal* utilities to their final state under
    // prefers-reduced-motion, so doing nothing here keeps the page
    // visible without any animation work.
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      // Make sure any .reveal / .reveal-fold elements are visible
      // even without the observer, in case they were rendered after
      // a route swap and their initial CSS still hides them.
      document
        .querySelectorAll<HTMLElement>('.reveal, .reveal-fold')
        .forEach((el) => el.classList.add('is-visible'));
      return;
    }

    // ── 1. reveal observer ──────────────────────────────────────────
    // One-shot reveal: once an element scrolls into view, add
    // .is-visible and stop watching it. We don't ever want elements
    // to fade back out as they leave the viewport.
    const revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
    );

    document
      .querySelectorAll<HTMLElement>('.reveal, .reveal-fold')
      .forEach((el) => revealObserver.observe(el));

    // ── 2. has-light + 3. data-tilt: pointer-driven CSS vars ────────
    // We attach per-element listeners (rather than one document-wide
    // mousemove) so React unmounts collect them automatically when
    // the elements go away. Both utilities share a single mousemove
    // handler per element, since they want the same {x, y} values.
    type Cleanup = () => void;
    const cleanups: Cleanup[] = [];

    function bindLight(el: HTMLElement) {
      const onMove = (e: MouseEvent) => {
        const rect = el.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        el.style.setProperty('--lx', `${x}%`);
        el.style.setProperty('--ly', `${y}%`);
      };
      const onEnter = () => el.classList.add('is-lit');
      const onLeave = () => el.classList.remove('is-lit');
      el.addEventListener('mousemove', onMove);
      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mouseleave', onLeave);
      cleanups.push(() => {
        el.removeEventListener('mousemove', onMove);
        el.removeEventListener('mouseenter', onEnter);
        el.removeEventListener('mouseleave', onLeave);
      });
    }

    function bindTilt(el: HTMLElement) {
      // Tilt magnitude in degrees; small so the effect feels
      // physical, not gimmicky. Inverted on Y so the card "leans
      // toward" the cursor instead of away.
      const MAX_DEG = 4;
      const onMove = (e: MouseEvent) => {
        const rect = el.getBoundingClientRect();
        const px = (e.clientX - rect.left) / rect.width - 0.5; // -0.5..0.5
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        const ry = px * 2 * MAX_DEG; // rotateY: left/right
        const rx = -py * 2 * MAX_DEG; // rotateX: up/down (inverted)
        el.style.transform = `perspective(900px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) translateY(-2px)`;
      };
      const onEnter = () => {
        // Bump the transition so the lean-in / reset feels smooth.
        // Include border-color + box-shadow because consumers
        // typically pair tilt with hover border/shadow changes —
        // setting a transform-only transition here would snap
        // those other props.
        el.style.transition =
          'transform 220ms cubic-bezier(0.22, 1, 0.36, 1), border-color 220ms ease-out, box-shadow 220ms ease-out';
      };
      const onLeave = () => {
        el.style.transform = '';
      };
      el.addEventListener('mousemove', onMove);
      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mouseleave', onLeave);
      cleanups.push(() => {
        el.removeEventListener('mousemove', onMove);
        el.removeEventListener('mouseenter', onEnter);
        el.removeEventListener('mouseleave', onLeave);
        el.style.transform = '';
        el.style.transition = '';
      });
    }

    function bindParallax(el: HTMLElement) {
      // Cursor-tracking radial gradient. Sets --mx/--my on the
      // element so a paired ::after gradient can read them.
      // Throttled to a single rAF per frame so big hero panels
      // don't burn the main thread on mousemove.
      let raf = 0;
      const onMove = (e: MouseEvent) => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const rect = el.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          el.style.setProperty('--mx', `${x}%`);
          el.style.setProperty('--my', `${y}%`);
        });
      };
      const onLeave = () => {
        // Snap glow back to centered rest state. The CSS transition
        // smooths the return so it doesn't pop.
        cancelAnimationFrame(raf);
        el.style.setProperty('--mx', '50%');
        el.style.setProperty('--my', '50%');
      };
      el.addEventListener('mousemove', onMove);
      el.addEventListener('mouseleave', onLeave);
      cleanups.push(() => {
        cancelAnimationFrame(raf);
        el.removeEventListener('mousemove', onMove);
        el.removeEventListener('mouseleave', onLeave);
      });
    }

    function bindGutter(el: HTMLElement) {
      // rAF-throttled to keep the bar buttery on long lists. Bar
      // is 36px tall; centering on the cursor means subtracting
      // half-height (18px) from the local Y.
      let raf = 0;
      const onMove = (e: MouseEvent) => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const rect = el.getBoundingClientRect();
          const y = e.clientY - rect.top - 18;
          el.style.setProperty('--gy', `${y}px`);
          el.classList.add('gutter-active');
        });
      };
      const onLeave = () => {
        cancelAnimationFrame(raf);
        el.classList.remove('gutter-active');
      };
      el.addEventListener('mousemove', onMove);
      el.addEventListener('mouseleave', onLeave);
      cleanups.push(() => {
        cancelAnimationFrame(raf);
        el.removeEventListener('mousemove', onMove);
        el.removeEventListener('mouseleave', onLeave);
      });
    }

    document
      .querySelectorAll<HTMLElement>('.has-light')
      .forEach(bindLight);
    document
      .querySelectorAll<HTMLElement>('[data-tilt]')
      .forEach(bindTilt);
    document
      .querySelectorAll<HTMLElement>('.has-cursor-gutter')
      .forEach(bindGutter);
    document
      .querySelectorAll<HTMLElement>('[data-parallax]')
      .forEach(bindParallax);

    // ── MutationObserver: pick up elements added after first render ─
    // Dashboards re-render their lists as queries resolve, so the
    // .has-light / .reveal / [data-tilt] elements often arrive a
    // tick after this hook fires. Watch the document for additions
    // and bind/observe them as they appear.
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          // The added node itself plus its descendants both need
          // checking — querySelectorAll on the node alone misses
          // the node itself even if it matches.
          const candidates: HTMLElement[] = [node];
          node.querySelectorAll<HTMLElement>(
            '.reveal, .reveal-fold, .has-light, .has-cursor-gutter, [data-tilt], [data-parallax]',
          ).forEach((el) => candidates.push(el));

          for (const el of candidates) {
            if (
              (el.classList.contains('reveal') ||
                el.classList.contains('reveal-fold')) &&
              !el.classList.contains('is-visible')
            ) {
              revealObserver.observe(el);
            }
            if (el.classList.contains('has-light')) {
              bindLight(el);
            }
            if (el.hasAttribute('data-tilt')) {
              bindTilt(el);
            }
            if (el.classList.contains('has-cursor-gutter')) {
              bindGutter(el);
            }
            if (el.hasAttribute('data-parallax')) {
              bindParallax(el);
            }
          }
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      revealObserver.disconnect();
      mo.disconnect();
      for (const fn of cleanups) fn();
    };
  }, []);
}
